import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { SubscriptionStatusBadge } from "@/components/ui/StatusBadge";
import { LoadingState, ErrorState, DemoDataNotice } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { formatCurrencyFromCents, formatDateTime } from "@/lib/format";
import type { BillingOverview, CreateCheckoutResponse, SelfServePlan } from "@platform/shared";

/**
 * /dashboard/billing -- the customer's own plan and subscription status.
 *
 * Everything shown is read from the API, which reads the `subscriptions`
 * row that verified Stripe webhooks keep up to date. "Subscribe" asks the
 * API for a Stripe Checkout URL and redirects there; no Stripe key, price
 * id or amount ever exists in the browser.
 *
 * Coming back from Checkout (`?checkout=success`) does NOT itself change
 * anything: the plan only updates once Stripe's webhook has been processed,
 * which usually takes a second or two, so the page re-polls briefly.
 */
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 8;

export function Billing() {
  const { data, isLoading, error, refetch } = useApi<BillingOverview>("/api/billing/subscription");
  const [searchParams] = useSearchParams();
  const checkout = searchParams.get("checkout");

  const [pendingPlan, setPendingPlan] = useState<SelfServePlan | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [polls, setPolls] = useState(0);

  const awaitingWebhook = checkout === "success" && data !== null && !data.subscription.hasStripeSubscription;

  useEffect(() => {
    if (!awaitingWebhook || polls >= MAX_POLLS) return;
    const timer = setTimeout(() => {
      setPolls((n) => n + 1);
      refetch();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [awaitingWebhook, polls, refetch]);

  async function startCheckout(plan: SelfServePlan) {
    setPendingPlan(plan);
    setActionError(null);
    try {
      const { url } = await apiFetch<CreateCheckoutResponse>("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ plan }),
      });
      window.location.assign(url);
    } catch (err) {
      setActionError(err instanceof ApiRequestError ? err.message : "Couldn't start checkout.");
      setPendingPlan(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Billing</h1>
        <p className="mt-1 text-sm text-ink-soft">Your plan and subscription status.</p>
      </div>

      {isLoading && !data && <LoadingState label="Loading billing…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {checkout === "success" && (
        <Card className="border-success-500/30 bg-success-50 px-5 py-4">
          <p className="text-sm text-success-500">
            {awaitingWebhook
              ? "Thanks! Waiting for payment confirmation from Stripe…"
              : "Thanks! Your subscription is set up."}
          </p>
        </Card>
      )}
      {checkout === "canceled" && (
        <Card className="px-5 py-4">
          <p className="text-sm text-ink-soft">Checkout was canceled — you haven't been charged.</p>
        </Card>
      )}

      {data && (
        <>
          <Card className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-ink-soft">Current plan</p>
                <p className="mt-1 font-display text-2xl text-ink">{data.subscription.planName}</p>
              </div>
              <SubscriptionStatusBadge status={data.subscription.status} />
            </div>

            <dl className="mt-4 space-y-2 border-t border-line pt-4 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-ink-soft">
                  {data.subscription.cancelAtPeriodEnd
                    ? "Access ends"
                    : data.subscription.status === "trialing"
                      ? "Trial ends"
                      : "Renews"}
                </dt>
                <dd className="font-medium text-ink">
                  {data.subscription.currentPeriodEnd ? formatDateTime(data.subscription.currentPeriodEnd) : "—"}
                </dd>
              </div>
            </dl>

            {data.subscription.status === "past_due" && (
              <p className="mt-4 rounded-md border border-signal-300 bg-signal-50 px-4 py-3 text-sm text-signal-600">
                Your last payment didn't go through. Stripe will retry automatically.
              </p>
            )}
            {data.subscription.cancelAtPeriodEnd && (
              <p className="mt-4 text-sm text-ink-soft">
                This subscription is set to cancel and won't renew.
              </p>
            )}
            {data.subscription.status === "canceled" && (
              <p className="mt-4 text-sm text-ink-soft">This subscription has ended. You can subscribe again below.</p>
            )}
          </Card>

          {(!data.subscription.hasStripeSubscription || data.subscription.status === "canceled") && (
            <div className="space-y-3">
              <h2 className="font-display text-lg text-ink">Choose a plan</h2>
              {actionError && <ErrorState message={actionError} />}
              <div className="grid gap-4 sm:grid-cols-2">
                {data.plans.map((plan) => (
                  <Card key={plan.plan} className="flex flex-col justify-between p-5">
                    <div>
                      <p className="font-medium text-ink">{plan.name}</p>
                      <p className="mt-2 font-display text-3xl text-ink">
                        {formatCurrencyFromCents(plan.monthlyPriceCents)}
                        <span className="text-sm text-ink-soft"> /month</span>
                      </p>
                    </div>
                    <Button
                      className="mt-4"
                      disabled={!data.canManageBilling || !plan.available || pendingPlan !== null}
                      onClick={() => startCheckout(plan.plan)}
                    >
                      {pendingPlan === plan.plan ? "Redirecting…" : `Subscribe to ${plan.name}`}
                    </Button>
                    {!plan.available && (
                      <p className="mt-2 text-xs text-ink-soft">Not available right now.</p>
                    )}
                  </Card>
                ))}
              </div>
              {!data.canManageBilling && (
                <p className="text-sm text-ink-soft">Only an organization owner or admin can change the plan.</p>
              )}
            </div>
          )}

          <DemoDataNotice>
            Payments run in Stripe <strong>test mode</strong> — use test card <code>4242 4242 4242 4242</code>{" "}
            with any future expiry and any CVC. No real money moves.
          </DemoDataNotice>
        </>
      )}
    </div>
  );
}
